/* specCheck.js -- THE upload check: every onboarding template, checked against its spec at PARSE, every problem named
 * at once by row and column, before anything is saved (W-068 pass 3, 2026-09-29).
 *
 * ONE FUNCTION, ONE SOURCE. checkAgainstSpec(template, parsed, context) reads:
 *   - the SPEC's own fields (templateSpecs.js): `required`, `type`, `values`, `valueAliases`, `valueCase` -- from these
 *     it DERIVES four rules for every column (B1 a required column missing - B2 a required cell blank - B3 a value
 *     outside the accepted list - B6 text in a number cell). There is no second list of those rules anywhere.
 *   - EXPLICIT_RULES below -- the rules the fields cannot express, each naming its spec column (or the whole sheet).
 *   - the parse itself: `parsed.specScan`, the rows and the header->column map the template's OWN parser found (the
 *     parsers record what they read; this module never re-detects a header, so there is no second header grammar).
 * The document (docs/ONBOARDING-TEMPLATES.md, column "What blocks the upload") is generated from specColumnRules /
 * specSheetRules -- the same functions this check runs -- and the spec gate forges every rule and asserts it fires.
 *
 * BRIAN'S PRINCIPLE (09-29): anything that today silently drops or misreads data BLOCKS with a clear message; a
 * legitimate blank stays allowed. A WARNING never blocks. The check runs ONLY on a fresh upload (Steps 2 / 5 / 6 / 8
 * parse handlers); no stored row is read or re-validated.
 *
 * WHAT IT DOES NOT CHECK: a LEGACY layout (a raw month-name export, the grouped prepaid layout) is not the template and
 * is read exactly as before -- except B7 (an asset sheet with no bs_line header: W-063 retired the old positional D&A
 * reader, so it is blocked, never read) and B10 (a sheet that carries the template's is_category header but cannot be
 * read as the template).
 *
 * Plain ASCII only (the generated document inherits the rule texts). */
import { TEMPLATE_SPECS, specValueLabels } from './templateSpecs';
import { looksLikeTotalRow } from './templateHeaders';
import { parseStartMonth } from './parseVendorExcel';
import { resolvePrepaidRowLine } from '../engines/prepaidRouting';
import { normalizeVendorType } from './vendorForecastTypes';

export const BLOCKING = 'blocking';
export const WARNING = 'warning';

const isBlank = (v) => v == null || (typeof v === 'string' && v.trim() === '');
const show = (v) => JSON.stringify(typeof v === 'string' ? v.trim() : v);

/* A number cell, read the way the template parsers read one: "$", ",", "%" and spaces are ignored, "(1,000)" is -1000,
 * a blank or "-" is 0. Anything else (e.g. "n/a", "12 months", a formula written as text) is NOT a number -- the parsers
 * would silently read it as 0 (or drop it), which is B6. */
const NUM_RE = /^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i;
export function readNumber(v) {
  if (isBlank(v)) return { ok: true, value: 0, blank: true };
  if (typeof v === 'number') return { ok: Number.isFinite(v), value: v };
  if (typeof v !== 'string') return { ok: false };
  const s = v.trim();
  const stripped = s.replace(/[$,()%\s]/g, '');
  if (stripped === '' || stripped === '-') return { ok: true, value: 0 };
  if (!NUM_RE.test(stripped)) return { ok: false };
  const n = parseFloat(stripped);
  return { ok: true, value: /\(.*\)/.test(s) ? -Math.abs(n) : n };
}

/* An enum value is accepted when it is one of the column's `values` (or `valueAliases`); upper/lower case does not
 * matter unless the column says `valueCase: 'exact'` (the vendor section, which Step 6 compares exactly). */
function acceptedValue(v, col) {
  const s = String(v).trim();
  const list = [...specValueLabels(col), ...(col.valueAliases || [])];
  return col.valueCase === 'exact' ? list.includes(s) : list.some(x => String(x).toLowerCase() === s.toLowerCase());
}

/* ---------------------------------------------------------------------------------------------------------------
 * THE EXPLICIT RULES -- the ones the spec's fields cannot express. `column: null` = a whole-sheet rule. The ids match
 * Brian's list (B1-B9, B10 = "an is_category header but no readable month columns" -- since 2026-11-02 also "month columns
 * that do not cover the company's months", F1-F3). */
export const EXPLICIT_RULES = [
  { id: 'B4', template: 'employee', column: 'State', severity: BLOCKING,
    text: 'a value that is not a two-letter US state code ("New York" is refused; write NY). A blank cell is allowed: the company default state is used.' },
  { id: 'B5', template: 'employee', column: 'Commission Rate', severity: BLOCKING,
    text: 'a rate outside 0-100% once read (a value above 1 is read as a percent, so 5000 reads as 5000%), or exactly 1 (1% or 100%? write the decimal: 0.01). A column of commission DOLLARS fails this check.' },
  /* W-063 (Brian 09-29): an asset's balance-sheet line is ALWAYS ASKED. B7 used to fire only when the old positional
   * reader found ZERO assets; a schedule that DID yield assets was imported and each one's line was GUESSED from its name
   * (the first line when nothing matched). That reader and the guess are retired, so any such sheet now blocks. */
  { id: 'B7', template: 'asset', column: null, severity: BLOCKING,
    text: 'a sheet with no header row naming both description and bs_line (for example an old position-based D&A schedule): it is not read. An asset\'s balance-sheet line is always asked -- never guessed from its name.' },
  { id: 'B8', template: 'prepaid', column: 'gl_line_name', severity: BLOCKING,
    text: 'a line -- or, when the cell is blank, the vendor name -- that is not exactly one income-statement line of this company (it would book nothing to the P&L). With is_category filled, the line must exist in THAT section (the same section + name rule a vendor row uses); with is_category blank, the name must match exactly one line across the whole income statement.' },
  { id: 'B9', template: 'asset', column: 'useful_life', severity: BLOCKING,
    text: 'a blank cell (there is no five-year default).' },
  { id: 'B10', template: 'is', column: null, severity: BLOCKING,
    text: 'an is_category header but no readable month column, or month columns that do not cover the months of this company (from the historical start to the close month; on the balance sheet also the opening month just before the start) -- the sheet cannot be read as the template.' },
  { id: 'B10', template: 'bs', column: null, severity: BLOCKING,
    text: 'an is_category header but no readable month column, or month columns that do not cover the months of this company (from the historical start to the close month; on the balance sheet also the opening month just before the start) -- the sheet cannot be read as the template.' },
  { id: 'F1', template: 'is', column: 'gl_line_name', severity: WARNING,
    text: 'a name that looks like a total ("total" or "subtotal" in it): KEPT, flagged, and pre-set to Skip at Step 3 as "suggested -- looks like a total row -- confirm or change".' },
  { id: 'F1', template: 'bs', column: 'gl_line_name', severity: WARNING,
    text: 'a name that looks like a total ("total" or "subtotal" in it): KEPT, flagged, and pre-set to Skip at Step 3 as "suggested -- looks like a total row -- confirm or change".' },
  { id: 'F1', template: 'vendor', column: 'vendor_name', severity: WARNING,
    text: 'a name that looks like a total ("total" or "subtotal" in it): KEPT and imported as a vendor (Step 6 cannot leave a vendor out) -- delete the row if it is a total.' },
  { id: 'F2', template: 'is', column: null, severity: WARNING,
    text: 'an expense line whose historical months add up to a negative, or an Other "...Income" line whose months add up to a positive (a refund or credit can be real).' },
  { id: 'F3', template: 'asset', column: 'cost', severity: WARNING,
    text: 'a cost of zero.' },
  /* W-063 (Brian 09-29): the model does not retire assets yet -- an asset listed with a past disposal date keeps
   * depreciating until its useful life ends. Disposals belong to the monthly close (a future module). The date is
   * read and shown, never stored. */
  { id: 'F5', template: 'asset', column: 'disposal_date', severity: WARNING,
    text: 'a filled disposal date: the model does not retire assets yet -- the asset keeps depreciating until its useful life ends (disposals are handled in the monthly close, a future module). Remove the row if the asset is gone.' },
  /* W-065 (Brian 09-29): the revenue TOTAL, never one line -- a single negative revenue line is real (discounts / refunds
   * are contra-revenue). Strictly negative, the same comparison as the Step-4b review's N4 warning (sum < 0). */
  { id: 'F4', template: 'is', column: null, severity: WARNING,
    text: 'every Revenue row, over the historical months, adds up to a NEGATIVE total -- the file may have revenue signs reversed (one negative revenue line, such as discounts or refunds, is fine).' },
  /* 2026-10-29 (Brian, SubBase) -- THE VENDOR-FORECAST FILE. A month cell must be a month (a text month that is not one would
   * leave the forecast's start, end or billing month unset -- silently); a type must carry what it needs (a Per Seat row with
   * no cost, or a % of Revenue row with no rate or base, would book $0); a Fixed Monthly row with no amount books $0 (a warning:
   * $0 is a legitimate answer, but write 0 to say so). Matching a row to a staged vendor is not judged here -- it needs the
   * staged vendors, so it happens on the Forecast sub-step, where an unmatched row is named (utils/vendorForecastFile.js). */
  { id: 'B11', template: 'vendor_forecast', column: 'billing_month', severity: BLOCKING,
    text: 'a filled cell that is not a month (write YYYY-MM).' },
  { id: 'B11', template: 'vendor_forecast', column: 'start_month', severity: BLOCKING,
    text: 'a filled cell that is not a month (write YYYY-MM).' },
  { id: 'B11', template: 'vendor_forecast', column: 'end_month', severity: BLOCKING,
    text: 'a filled cell that is not a month (write YYYY-MM, or Until Canceled).' },
  { id: 'B12', template: 'vendor_forecast', column: 'forecast_type', severity: BLOCKING,
    text: 'a Per Seat row with no per_seat_cost above 0, or a % of Revenue row with no pct_rate or no pct_base (it would forecast $0).' },
  { id: 'F6', template: 'vendor_forecast', column: 'amount', severity: WARNING,
    text: 'a Fixed Monthly row with a blank amount: it forecasts $0 in every forecast month (write 0 to say so).' },
];

/* The rules that apply to ONE column: derived from its spec fields, then the explicit ones naming it. The generated
 * document's "What blocks the upload" column is exactly this list. */
export function specColumnRules(templateKey, col) {
  if (!col) return [];
  const explicit = EXPLICIT_RULES.filter(r => r.template === templateKey && r.column === col.name);
  // A column that is read but not stored (imported: false) derives nothing -- only its explicit rules (F5).
  if (col.imported === false) return explicit.map(r => ({ id: r.id, severity: r.severity, text: r.text }));
  const out = [];
  if (col.required === 'column' || col.required === 'cell') out.push({ id: 'B1', severity: BLOCKING, text: 'the column is missing.' });
  if (col.required === 'cell' && !explicit.some(r => r.id === 'B9')) {
    out.push({ id: 'B2', severity: BLOCKING, text: col.type === 'month' ? 'a blank cell, or a value that is not a month, on a row that has data.' : 'a blank cell on a row that has data.' });
  }
  if (col.type === 'enum' && col.values) {
    out.push({ id: 'B3', severity: BLOCKING, text: 'a value that is not one of the accepted values'
      + (col.valueCase === 'exact' ? ' (exact spelling, case included)' : ' (upper/lower case does not matter)')
      + (col.required === 'cell' ? '.' : '; a blank cell is allowed.') });
  }
  if (col.type === 'number') out.push({ id: 'B6', severity: BLOCKING, text: 'text in the cell ("n/a"); a blank, "-", "$1,000" and "(1,000)" are fine.' });
  return [...out, ...explicit.map(r => ({ id: r.id, severity: r.severity, text: r.text }))];
}

/* The whole-sheet rules of one template (+ B6 on the month cells when the template has months). */
export function specSheetRules(templateKey) {
  const spec = TEMPLATE_SPECS[templateKey];
  const out = [];
  if (spec && spec.months !== 'none') out.push({ id: 'B6', severity: BLOCKING, text: 'text in a month cell ("n/a"); a blank, "-", "$1,000" and "(1,000)" are fine.' });
  return [...out, ...EXPLICIT_RULES.filter(r => r.template === templateKey && r.column === null).map(r => ({ id: r.id, severity: r.severity, text: r.text }))];
}

/* ---------------------------------------------------------------------------------------------------------------
 * ONE TEST PER RULE: (value, col, rc) -> a problem sentence, or null. `rc` = { templateKey, context, get(name) }.
 * B1 (a missing column) and the whole-sheet rules are tested outside the row loop. A rule named by the spec or by
 * EXPLICIT_RULES with no test here THROWS -- a dropped test must never be a silent pass (the spec gate plants one). */
const CELL_TESTS = {
  B2: (v, col) => {
    if (isBlank(v)) return 'is blank.';
    if (col.type === 'month' && !parseStartMonth(v)) return `${show(v)} is not a month (write YYYY-MM).`;
    return null;
  },
  B3: (v, col) => (!isBlank(v) && !acceptedValue(v, col)
    ? `${show(v)} is not one of: ${specValueLabels(col).join(' / ')}${col.valueCase === 'exact' ? ' (exactly as written)' : ''}.` : null),
  B4: (v, col) => (!isBlank(v) && !col.values.includes(String(v).trim().toUpperCase())
    ? `${show(v)} is not a two-letter US state code (e.g. CA, NY).` : null),
  B5: (v) => {
    if (isBlank(v)) return null;
    const r = readNumber(v);
    if (!r.ok) return null;                                    // text is B6's to name
    if (r.value === 1) return `${show(v)} -- is that 1% or 100%? Write the rate as a decimal (0.01 for 1%).`;
    const rate = r.value > 1 ? r.value / 100 : r.value;
    return (rate < 0 || rate > 1) ? `${show(v)} reads as ${Math.round(rate * 10000) / 100}%, outside 0-100%. A commission rate is a percentage (write 0.08 for 8%) -- is this a column of commission dollars?` : null;
  },
  B6: (v) => (!isBlank(v) && !readNumber(v).ok ? `${show(v)} is text, not a number.` : null),
  B8: (v, col, rc) => {
    const map = rc.context && rc.context.prepaidLabelToLine;
    if (!map) return null;                                     // no company lines supplied -> not judged here
    const account = isBlank(v) ? rc.get('vendor_name') : String(v).trim();
    if (isBlank(account)) return null;                         // no vendor either: B2 on vendor_name names it
    /* 2026-10-13 (Brian, SubBase): a row that names its section (is_category) is judged by the SAME rule the import uses --
     * the ONE owner, resolvePrepaidRowLine: section + name is the vendor rule's key, and it must be a line of this
     * company. A row with no section is judged by the name alone, worded exactly as before. An is_category that is not
     * one of the sections is B3's to name (on is_category), so it is not named a second time here. */
    const section = rc.get('is_category');
    const res = resolvePrepaidRowLine({ account: String(account).trim(), is_category: isBlank(section) ? '' : String(section).trim() }, map);
    if (res.reason === 'label' || res.reason === 'section' || res.reason === 'bad-section') return null;
    const via = isBlank(v) ? ` (the cell is blank, so the vendor name ${show(account)} is used)` : '';
    if (res.reason === 'section-no-match') {
      return `${show(account)}${via} is not an income-statement line of this company in section ${show(res.section)} (the row's is_category), so this contract would book nothing to the P&L. Correct the section or the line name -- together they must name one line of the income statement.`;
    }
    return res.reason === 'ambiguous'
      ? `${show(account)}${via} matches ${res.ids.length} income-statement lines of this company -- it must match exactly one.`
      : `${show(account)}${via} is not an income-statement line of this company, so this contract would book nothing to the P&L.`;
  },
  B9: (v) => (isBlank(v) ? 'is blank -- enter the useful life in whole years (there is no default).' : null),
  F1: (v, col, rc) => {
    if (isBlank(v) || !looksLikeTotalRow(v)) return null;
    return rc.templateKey === 'vendor'
      ? `${show(v)} looks like a total row -- it is kept and imported as a vendor (Step 6 cannot leave a vendor out). If it is a total, delete the row from the file and re-upload.`
      : `${show(v)} looks like a total row -- it is kept, and pre-set to Skip at Step 3 ("suggested -- looks like a total row -- confirm or change").`;
  },
  F3: (v) => { const r = readNumber(v); return (!isBlank(v) && r.ok && r.value === 0) ? 'the cost is 0.' : null; },
  // F4 takes the sheet's revenue TOTAL (see checkAgainstSpec). Strictly negative: a total of exactly 0 does not fire.
  F5: (v, name) => (!isBlank(v)
    ? `${name ? show(name) + ' has' : 'this row has'} a disposal date: the model does not retire assets yet, so this asset will keep depreciating until its useful life ends (disposals are handled in the monthly close, a future module). Remove the row if the asset is gone. A warning -- continue if it is correct.`
    : null),
  F4: (total) => (total < 0
    ? `Total revenue adds up to -$${Math.abs(total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} over the historical months -- the file may have revenue signs reversed (revenue should be positive; discounts and refunds negative). A warning -- continue if it is correct.`
    : null),
  // 2026-10-29: the vendor-forecast file (see EXPLICIT_RULES).
  B11: (v, col) => {
    if (isBlank(v)) return null;
    if (col.name === 'end_month' && /^until[\s_-]*cancel+ed$/i.test(String(v).trim())) return null;
    return parseStartMonth(v) ? null : `${show(v)} is not a month (write YYYY-MM${col.name === 'end_month' ? ', or Until Canceled' : ''}).`;
  },
  B12: (v, col, rc) => {
    const type = normalizeVendorType(v);
    if (type === 'per_seat') {
      const c = readNumber(rc.get('per_seat_cost'));
      return (isBlank(rc.get('per_seat_cost')) || !c.ok || !(c.value > 0)) ? 'a Per Seat row needs a per_seat_cost above 0 (it would forecast $0).' : null;
    }
    if (type === 'pct_revenue') {
      const miss = [isBlank(rc.get('pct_rate')) ? 'pct_rate' : null, isBlank(rc.get('pct_base')) ? 'pct_base' : null].filter(Boolean);
      return miss.length ? `a % of Revenue row needs ${miss.join(' and ')} (it would forecast $0).` : null;
    }
    return null;
  },
  F6: (v, col, rc) => ((isBlank(v) && normalizeVendorType(rc.get('forecast_type')) === 'constant')
    ? 'a Fixed Monthly row with a blank amount forecasts $0 in every forecast month -- write 0 to say so. A warning -- continue if it is correct.' : null),
};

const where = (row, column) => `${row ? `Row ${row}` : 'Sheet'}${column ? `, ${column}` : ''}`;

/**
 * Check one parsed template against its spec.
 * @param {string} templateKey  'is' | 'bs' | 'vendor' | 'prepaid' | 'employee' | 'asset'
 * @param {object} parsed       the template parser's result for that sheet (carries `specScan`; the asset result also
 *                              `canonical` + its asset lists)
 * @param {object} [context]    { prepaidLabelToLine } -- the company's Step-3 lines (buildPrepaidLabelToLine), for B8
 * @returns {Array<{template, rule, severity, row, column, message}>} EVERY problem, blocking and warning, in sheet order
 */
export function checkAgainstSpec(templateKey, parsed, context = {}) {
  const spec = TEMPLATE_SPECS[templateKey];
  const out = [];
  if (!spec || !parsed) return out;
  const push = (rule, row, column, text) => out.push({ template: templateKey, rule: rule.id, severity: rule.severity, row, column, message: `${where(row, column)}: ${text}` });
  const sheetRule = (id) => specSheetRules(templateKey).find(r => r.id === id);
  const test = (id) => { const t = CELL_TESTS[id]; if (!t) throw new Error(`specCheck: rule ${id} has no test`); return t; };

  // B7 -- an asset sheet with no bs_line header (W-063: not read at all; its lines would have had to be guessed).
  if (templateKey === 'asset' && parsed.canonical === false) {
    const r = sheetRule('B7'); if (!r) throw new Error('specCheck: rule B7 has no entry');
    push(r, null, null, 'the sheet has no header row naming both description and bs_line, so it cannot be read as the asset template (an old position-based D&A schedule is not read -- an asset\'s balance-sheet line is always asked, never guessed). Use the template\'s columns: description, type, cost, in_service_date, useful_life, bs_line (optional: disposal_date, salvage_value, method).');
  }
  const scan = parsed.specScan;
  if (!scan) return out;

  const cols = scan.cols || {};
  const headerRowNum = scan.headerRow + 1;
  const imported = spec.columns.filter(c => c.imported !== false);
  // B1 -- a required column missing (from `required`).
  for (const col of imported) {
    if ((col.required === 'column' || col.required === 'cell') && cols[col.name] == null) {
      push({ id: 'B1', severity: BLOCKING }, headerRowNum, col.name, `the column is missing (the header row is row ${headerRowNum}).`);
    }
  }
  // B10 -- the template's is_category header is there, but the sheet cannot be read as the template.
  if (scan.layout === 'broken') {
    const n = Object.keys(scan.monthCols || {}).length;
    /* 2026-11-02 (the one-month start): a canonical sheet is read with ONE month column (parseFinancialExcel), so "broken"
     * here means no readable month column at all. It read `n < 3` ("only N readable month columns (3 or more are needed)"). */
    if ((templateKey === 'is' || templateKey === 'bs') && n < 1) {
      const r = sheetRule('B10'); if (!r) throw new Error('specCheck: rule B10 has no entry');
      push(r, headerRowNum, null, `the header row has is_category but no readable month column, so the sheet cannot be read as the template. Head each month column with its month as text "YYYY-MM".`);
    }
    return out;                                                // the rows of an unreadable sheet are not judged
  }
  /* B10, THE WINDOW (2026-11-02) -- the bare ">= 3 month columns" guard is replaced by the question it stood for: does the file
   * cover THIS company's months? `context.window` = the months the company's own template carries (templateSpecs
   * buildHistoricalMonths: historical start .. close; the balance sheet also the opening month). Passed by the Step-2 upload;
   * absent (a caller with no company) = not judged. Every missing month is named. Extra months are not refused (they were
   * never refused before). */
  if ((templateKey === 'is' || templateKey === 'bs') && Array.isArray(context.window) && context.window.length) {
    const have = new Set(Object.values(scan.monthCols || {}));
    const missing = context.window.filter(m => !have.has(m));
    if (missing.length) {
      const r = sheetRule('B10'); if (!r) throw new Error('specCheck: rule B10 has no entry');
      const w = context.window, span = w.length === 1 ? w[0] : `${w[0]} to ${w[w.length - 1]}`;
      const what = templateKey === 'bs' ? `the balance sheet needs one column per month from ${span} (the month before the historical start is the opening balance sheet)` : `the income statement needs one column per month from ${span}`;
      push(r, headerRowNum, null, `the month columns do not cover this company's months -- missing ${missing.join(', ')}; ${what}. Add the missing month column${missing.length === 1 ? '' : 's'}, headed with the month as text "YYYY-MM".`);
    }
  }

  const monthCols = Object.entries(scan.monthCols || {}).map(([c, m]) => [Number(c), m]);
  const extra = scan.extraNumberCols || [];
  const present = imported.filter(c => cols[c.name] != null);
  /* WHICH CELLS MAKE A ROW "DATA". The prepaid tab's section cell (is_category, read since 2026-10-13) does not: a row
   * carrying nothing but a section has no contract in it -- the parser reads nothing from it -- so it stays a blank row,
   * and a vendor-name-only row with a section beside it stays the old grouped layout's heading, exactly as before the
   * column was read. (The vendor tab's is_category is a required cell and is unchanged.) */
  const dataCols = present.filter(c => !(templateKey === 'prepaid' && c.name === 'is_category'));
  const considered = [...dataCols.map(c => cols[c.name]), ...monthCols.map(([c]) => c), ...extra.map(x => x.col)];
  const rulesOf = new Map(present.map(c => [c.name, specColumnRules(templateKey, c).filter(r => r.id !== 'B1')]));
  const monthRule = sheetRule('B6');
  let groupVendor = '';                                        // the old grouped prepaid layout's heading vendor

  for (let i = scan.headerRow + 1; i < scan.rows.length; i++) {
    const r = scan.rows[i] || [];
    if (considered.every(c => isBlank(r[c]))) continue;       // a blank row is not data
    const rowNum = i + 1;
    let get = (name) => (cols[name] == null ? undefined : r[cols[name]]);
    if (templateKey === 'prepaid') {
      // A row with ONLY a vendor name is a group heading (the old grouped layout): it names the vendor of the contract
      // rows under it and is not a contract itself.
      const vIdx = cols.vendor_name;
      if (vIdx != null && !isBlank(r[vIdx]) && considered.every(c => c === vIdx || isBlank(r[c]))) { groupVendor = String(r[vIdx]).trim(); continue; }
      if (vIdx != null && isBlank(r[vIdx]) && groupVendor) {
        const heading = groupVendor, base = get;                // this row's heading vendor, captured per row
        get = (name) => (name === 'vendor_name' ? heading : base(name));
      }
    }
    const rc = { templateKey, context, get };
    for (const col of present) {
      for (const rule of rulesOf.get(col.name)) {
        const msg = test(rule.id)(get(col.name), col, rc);
        if (msg) push(rule, rowNum, col.name, msg);
      }
    }
    if (monthRule) for (const [c, m] of monthCols) { const msg = test('B6')(r[c]); if (msg) push(monthRule, rowNum, m, msg); }
    for (const x of extra) { const msg = test('B6')(r[x.col]); if (msg) push({ id: 'B6', severity: BLOCKING }, rowNum, x.name, msg); }
  }

  // F2 -- the income statement's sign convention, over the historical months (a warning: refunds and credits are real).
  const f2 = sheetRule('F2');
  if (f2 && cols.is_category != null && cols.gl_line_name != null) {
    const EXPENSE = ['cogs', 's&m', 'g&a', 'r&d'];
    for (let i = scan.headerRow + 1; i < scan.rows.length; i++) {
      const r = scan.rows[i] || [];
      const cat = isBlank(r[cols.is_category]) ? '' : String(r[cols.is_category]).trim().toLowerCase();
      const name = isBlank(r[cols.gl_line_name]) ? '' : String(r[cols.gl_line_name]).trim();
      if (!name || !cat) continue;
      const total = Math.round(monthCols.reduce((s, [c]) => { const x = readNumber(r[c]); return s + (x.ok ? x.value : 0); }, 0) * 100) / 100;
      const income = cat === 'other' && /income\s*$/i.test(name);
      if (income && total > 0) push(f2, i + 1, 'gl_line_name', `${show(name)} is an Other "...Income" line whose months add up to +${total}; income lines are entered as NEGATIVE numbers (credits). A warning -- continue if it is correct.`);
      else if (!income && (EXPENSE.includes(cat) || cat === 'other') && total < 0) push(f2, i + 1, 'gl_line_name', `${show(name)} is an expense line whose months add up to ${total}; expenses are entered as positive numbers. A warning -- continue if it is correct (a refund or credit).`);
    }
  }
  // F4 (W-065) -- the SUM of every Revenue row over the historical months; one warning for the sheet, never per line.
  const f4 = sheetRule('F4');
  if (f4 && cols.is_category != null) {
    let revenue = 0;
    for (let i = scan.headerRow + 1; i < scan.rows.length; i++) {
      const r = scan.rows[i] || [];
      if (isBlank(r[cols.is_category]) || String(r[cols.is_category]).trim().toLowerCase() !== 'revenue') continue;
      revenue += monthCols.reduce((s, [c]) => { const x = readNumber(r[c]); return s + (x.ok ? x.value : 0); }, 0);
    }
    const msg = test('F4')(Math.round(revenue * 100) / 100);
    if (msg) push(f4, null, null, msg);
  }

  // F5 -- a filled disposal date (a column that is read but not stored, so it is outside the row loop's rules).
  const f5 = templateKey === 'asset' && cols.disposal_date != null
    ? specColumnRules('asset', spec.columns.find(c => c.name === 'disposal_date')).find(r => r.id === 'F5') : null;
  if (f5) {
    for (let i = scan.headerRow + 1; i < scan.rows.length; i++) {
      const r = scan.rows[i] || [];
      if (considered.every(c => isBlank(r[c]))) continue;     // a blank row is not data (the same test as the loop)
      const name = cols.description == null || isBlank(r[cols.description]) ? '' : String(r[cols.description]).trim();
      const msg = test('F5')(r[cols.disposal_date], name);
      if (msg) push(f5, i + 1, 'disposal_date', msg);
    }
  }
  return out;
}

/* ---------------------------------------------------------------------------------------------------------------
 * SHOWING THE PROBLEMS in each step's EXISTING review list (no new renderer). A problem becomes a flag of type
 * 'spec_check' carrying `severity` ('blocking' | 'warning'); a parser flag that says the same thing about the same row
 * and column is dropped, so one fault is named once. */
const PARSER_FLAG_COLUMN = {
  no_display_group_column: 'display_group', no_gl_line_name_column: 'gl_line_name', no_prepaid_gl_line_name_column: 'gl_line_name',
  unknown_category: 'is_category', no_salary_column: 'Salary', missing_salary: 'Salary', missing_department: 'Department',
  missing_date: 'in_service_date', zero_cost: 'cost', missing_life: 'useful_life',
};
export function mergeSpecProblems(flags, problems, extra = {}) {
  // keyed on the SHEET too: the vendor and prepaid tabs share one flag list at Step 6, and their row numbers overlap
  const sheet = extra.sheet || '';
  const taken = new Set((problems || []).map(p => `${sheet}|${p.row}|${p.column}`));
  const kept = (flags || []).filter(f => { const c = f.column || PARSER_FLAG_COLUMN[f.type]; return !(c && taken.has(`${f.sheet || ''}|${f.row}|${c}`)); });
  const asFlags = (problems || []).map(p => ({ type: 'spec_check', severity: p.severity, row: p.row, column: p.column, rule: p.rule, label: p.message, message: p.message, ...extra }));
  return [...asFlags.filter(f => f.severity === BLOCKING), ...asFlags.filter(f => f.severity !== BLOCKING), ...kept];
}
export function countBlocking(flags) { return (flags || []).filter(f => f && f.severity === BLOCKING).length; }
/* THE ONE HEADING every step shows above its list when the file has blocking problems. */
export function blockingHeading(n) {
  return n ? `${n} problem${n === 1 ? '' : 's'} in this file must be fixed before it can be imported -- correct the file and re-upload it.` : null;
}
